/**
 * Création d'une auto-évaluation — Lot 4a.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  domainsApi,
  ApiRequestError,
  type DomainDto,
  type ApplicationDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { useAuth } from "../context/AuthContext";
import { localeLabel } from "../utils/locale";

export function AssessmentEntryPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.accessLevel ?? 0) >= 100;

  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [domain, setDomain] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, e] = await Promise.all([domainsApi.list(), assessmentApi.listErp()]);
        if (cancelled) return;
        let list = d.domains.filter((x) => x.active);
        if (!isAdmin && user?.domains?.length) {
          const allowed = new Set(user.domains.map((c) => c.toUpperCase()));
          list = list.filter((x) => allowed.has(x.code.toUpperCase()));
        }
        setDomains(list);
        setApplications(e.applications);
        if (list[0]) setDomain(list[0].code);
        // Application is optional — default to empty (no application)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user?.domains]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!domain) {
      setError(t("required_domain"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { assessment } = await assessmentApi.create({
        domain,
        applicationId: applicationId || undefined,
        year,
      });
      navigate(`/assessments/${assessment.id}`);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === "ASSESSMENT_EXISTS") {
        const id = err.details?.assessmentId;
        setError(
          id
            ? t("session_exists_id", { id: String(id).slice(0, 8) })
            : err.message
        );
      } else {
        setError(err instanceof ApiRequestError ? err.message : t("create_failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sm-page">
      <PageHeader
        title={t("new_assessment")}
        subtitle={t("new_assessment_sub")}
      />

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="sm-form-card" style={{ maxWidth: 520 }}>
        <label className="sm-muted">{t("domain")}</label>
        <select
          className="sm-input"
          required
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          style={{ marginBottom: 12, width: "100%" }}
        >
          <option value="">—</option>
          {domains.map((d) => (
            <option key={d.id} value={d.code}>
              {d.code} — {localeLabel(i18n.language, d)}
            </option>
          ))}
        </select>

        <label className="sm-muted">
          {t("application")}
          <span className="sm-muted" style={{ fontWeight: "normal", marginLeft: 6, fontSize: "0.85em" }}>
            ({t("optional")})
          </span>
        </label>
        <select
          className="sm-input"
          value={applicationId}
          onChange={(e) => setApplicationId(e.target.value)}
          style={{ marginBottom: 12, width: "100%" }}
        >
          <option value="">{t("no_application")}</option>
          {applications.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>

        <label className="sm-muted">{t("year")}</label>
        <input
          type="number"
          className="sm-input"
          required
          min={2020}
          max={2100}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ marginBottom: 16, width: "100%", maxWidth: 160 }}
        />

        <div className="sm-form-actions">
          <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
            {t("create_and_score")}
          </button>
          <button
            type="button"
            className="sm-btn sm-btn-ghost"
            disabled={busy}
            onClick={() => navigate("/assessments")}
          >
            {t("cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
